import { z } from "zod";
import { isoDateSchema, uuidSchema } from "./domain";

/**
 * Memo of the calendar (ADR 0076): something a member has to remember every
 * day from `startsOn` until "Fatto". No time, and nothing ever goes to the lead.
 */
export const MEMO_TEXT_MAX = 500;
/** "Da quando ricordarlo" goes at most a year ahead. */
export const MEMO_STARTS_MAX_DAYS = 366;

const memoTextSchema = z
  .string()
  .trim()
  .min(1, "Scrivi cosa ricordare.")
  .max(MEMO_TEXT_MAX, `Al massimo ${MEMO_TEXT_MAX} caratteri.`);
const versionSchema = z.number().int().positive();

export const memoInputSchema = z
  .object({
    text: memoTextSchema,
    /** Who has to remember it: an active member who is not a reviewer. */
    memberId: uuidSchema,
    leadId: uuidSchema.nullable(),
    /** Left out: today in the workspace time zone. */
    startsOn: isoDateSchema.optional(),
  })
  .strict();
export type MemoInput = z.infer<typeof memoInputSchema>;

/** "Modifica": the text, who has it, the lead and the first day. */
export const memoUpdateInputSchema = z
  .object({
    version: versionSchema,
    text: memoTextSchema.optional(),
    memberId: uuidSchema.optional(),
    leadId: uuidSchema.nullable().optional(),
    startsOn: isoDateSchema.optional(),
  })
  .strict();
export type MemoUpdateInput = z.infer<typeof memoUpdateInputSchema>;

/** "Rimanda": from a day after today. */
export const memoPostponeInputSchema = z
  .object({ version: versionSchema, startsOn: isoDateSchema })
  .strict();
export type MemoPostponeInput = z.infer<typeof memoPostponeInputSchema>;

export const memoVersionInputSchema = z.object({ version: versionSchema }).strict();

export type MemoMemberDto = { id: string; name: string };

export type MemoDto = {
  id: string;
  text: string;
  /** Who has to remember it. */
  member: MemoMemberDto;
  createdBy: MemoMemberDto;
  lead: { id: string; displayName: string; companyName: string | null } | null;
  /** `YYYY-MM-DD` in the workspace time zone. */
  startsOn: string;
  doneAt: string | null;
  doneBy: MemoMemberDto | null;
  createdAt: string;
  version: number;
};

export type MemoListDto = {
  timezone: string;
  /** Today in the workspace time zone, `YYYY-MM-DD`. */
  today: string;
  /** To remember today: started and not done, oldest first. */
  due: MemoDto[];
  /** Not done, from a day after today. */
  upcoming: MemoDto[];
  /** Done in the last days, newest first. */
  done: MemoDto[];
};

/** The day `days` after a `YYYY-MM-DD` day; null when it is not a real day. */
export function addDaysToIsoDate(value: string, days: number): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** "Rimanda" a domani or to the next Monday, both after `today`. */
export function memoPostponeDays(today: string): { tomorrow: string; monday: string } {
  const tomorrow = addDaysToIsoDate(today, 1) ?? today;
  const weekday = new Date(`${today}T00:00:00Z`).getUTCDay();
  const toMonday = (8 - weekday) % 7 || 7;
  return { tomorrow, monday: addDaysToIsoDate(today, toMonday) ?? tomorrow };
}
